/**
 * Sidebar Component
 * Re-implemented from scratch to ensure clean state and correct scrolling behavior.
 */

export class Sidebar {
    constructor(containerId, options = {}) {
        this.containerId = containerId;
        this.options = options;
        this.isOpen = false;
        this.sidebarId = 'sidebar';
        this.toggleId = 'sidebarToggle';
        this.overlayId = 'sidebar-overlay';
        this.element = null;
        this.overlay = null;
    }

    init() {
        // 0. Prepare Container (Clear any static HTML)
        console.log('Sidebar initializing in container:', this.containerId);
        const container = document.getElementById(this.containerId);
        if (container) {
            container.innerHTML = '';
        }

        // 1. Clean up any existing instances (just in case)
        const existing = document.getElementById(this.sidebarId);
        if (existing) existing.remove();
        const existingOverlay = document.getElementById(this.overlayId);
        if (existingOverlay) existingOverlay.remove();
        const existingStyle = document.getElementById('sidebar-styles');
        if (existingStyle) existingStyle.remove();

        // 2. Inject fresh CSS
        this.injectStyles();

        // 3. Create DOM elements
        this.createElements(container);

        // 4. Render Menu Content
        this.render();

        // 5. Attach Events
        this.attachEvents();
    }

    /**
     * Highlights the active menu item based on ID
     */
    setActive(id) {
        this.element.querySelectorAll('.nav-item').forEach(item => {
            item.classList.toggle('active', item.dataset.id === id);
        });
    }

    injectStyles() {
        const css = `
            /* Main Content Background */
            body, .main-container {
                background-color: #ffffff;
            }

            /* Sidebar Container */
            #sidebar {
                position: fixed;
                top: 60px; /* Below TopNav */
                left: 0;
                width: 60px;
                height: calc(100vh - 60px);
                background-color: #f5f6f8;
                color: inherit;
                transform: translateX(0);
                transition: width 0.2s ease-in-out, transform 0.2s ease-in-out;
                z-index: 9999;
                display: flex;
                flex-direction: column;
                
                /* Scrolling Logic */
                overflow-y: auto;
                overscroll-behavior: contain; /* Prevents scroll chaining to body */
                
                /* Scrollbar Styling - High Visibility */
                scrollbar-width: thin;
                scrollbar-color: #ccc transparent;
            }

            /* Mobile: Hidden by default */
            @media (max-width: 768px) {
                #sidebar {
                    width: 260px;
                    transform: translateX(-100%);
                }
            }

            #sidebar.open {
                width: 260px;
                transform: translateX(0);
            }

            /* Collapsed State (Desktop) */
            #sidebar:not(.open) .nav-text {
                display: none;
            }
            #sidebar:not(.open) .nav-icon {
                margin-right: 0;
            }
            #sidebar:not(.open) .nav-item {
                justify-content: center;
                padding-left: 0;
                padding-right: 0;
            }
            #sidebar:not(.open) #sidebarClose {
                display: none;
            }
            #sidebar:not(.open) {
                overflow: visible;
            }
            /* Hide Main Section (middle) when collapsed */
            #sidebar:not(.open) .sidebar-section:not(.top-section):not(:last-child) {
                display: none;
            }

            /* Webkit Scrollbar (Chrome/Safari/Edge) */
            #sidebar::-webkit-scrollbar {
                width: 10px;
                display: block; /* Force display */
            }
            #sidebar::-webkit-scrollbar-track {
                background: transparent;
            }
            #sidebar::-webkit-scrollbar-thumb {
                background-color: #ccc;
                border-radius: 5px;
                border: 2px solid #f5f6f8;
            }
            #sidebar::-webkit-scrollbar-thumb:hover {
                background-color: #999;
            }

            /* Overlay */
            #sidebar-overlay {
                position: fixed;
                top: 60px;
                left: 0;
                width: 100vw;
                height: calc(100vh - 60px);
                background: transparent;
                z-index: 900;
                opacity: 0;
                visibility: hidden;
                transition: opacity 0.2s ease;
                pointer-events: none;
            }
            #sidebar-overlay.active {
                opacity: 1;
                visibility: visible;
                pointer-events: auto;
            }

            /* Close Button */
            #sidebarClose {
                position: absolute;
                top: 15px;
                right: 15px;
                width: 24px;
                height: 24px;
                cursor: pointer;
                color: #007bff;
                z-index: 20;
            }

            /* Open Button */
            #sidebarOpen {
                position: absolute;
                top: 15px;
                left: 50%;
                transform: translateX(-50%);
                width: 24px;
                height: 24px;
                cursor: pointer;
                color: #007bff;
                z-index: 20;
                display: none;
            }
            #sidebar:not(.open) #sidebarOpen {
                display: block;
            }

            /* Custom Tooltip */
            .tooltip-text {
                position: absolute;
                top: 100%;
                right: 0;
                margin-top: 8px;
                background-color: #000;
                color: #fff;
                padding: 6px 10px;
                border-radius: 6px;
                font-size: 13px;
                font-weight: 700;
                white-space: nowrap;
                opacity: 0;
                visibility: hidden;
                transition: opacity 0.15s ease;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                pointer-events: none;
            }

            #sidebarOpen .tooltip-text {
                top: 50%;
                left: 100%;
                right: auto;
                margin-top: 0;
                margin-left: 10px;
                transform: translateY(-50%);
            }

            .nav-item .tooltip-text {
                top: 50%;
                left: 100%;
                right: auto;
                margin-top: 0;
                margin-left: 10px;
                transform: translateY(-50%);
            }

            #sidebarClose:hover .tooltip-text,
            #sidebarClose:focus .tooltip-text,
            #sidebarOpen:hover .tooltip-text,
            #sidebarOpen:focus .tooltip-text,
            #sidebar:not(.open) .nav-item:hover .tooltip-text {
                opacity: 1;
                visibility: visible;
            }

            /* Menu Items */
            .sidebar-section {
                padding: 5px 10px;
                border-bottom: 1px solid #e5e5e5;
            }
            .sidebar-section.top-section {
                margin-top: 40px;
            }
            .sidebar-section:last-child {
                border-bottom: none;
                margin-top: auto;
            }

            .nav-item {
                position: relative;
                display: flex;
                align-items: center;
                padding: 10px 12px;
                margin-bottom: 2px;
                border-radius: 6px;
                cursor: pointer;
                color: inherit;
                text-decoration: none;
                font-size: 19px;
                user-select: none;
            }
            .nav-item:hover {
                background-color: #e4e5e7;
            }
            .nav-item.active {
                background-color: #dcdedf;
            }
            
            .nav-icon {
                width: 20px;
                height: 20px;
                margin-right: 12px;
                stroke: currentColor;
                flex-shrink: 0;
            }
            
            /* See More / Hidden Menu */
            #moreMenu {
                display: none;
            }
            #moreMenu.visible {
                display: block;
            }
        `;
        
        const style = document.createElement('style');
        style.id = 'sidebar-styles';
        style.textContent = css;
        document.head.appendChild(style);
    }

    createElements(container) {
        // Sidebar
        this.element = document.createElement('aside');
        this.element.id = this.sidebarId;
        if (container) {
            container.appendChild(this.element);
        } else {
            document.body.appendChild(this.element);
        }

        // Overlay
        this.overlay = document.createElement('div');
        this.overlay.id = this.overlayId;
        document.body.appendChild(this.overlay);
    }

    render() {
        // Icons
        const icons = {
            home: '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline>',
            msg: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>',
            ai: '<path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"></path>',
            stream: '<rect x="2" y="7" width="20" height="15" rx="2" ry="2"></rect><polyline points="17 2 12 7 7 2"></polyline>',
            cup: '<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path>',
            shop: '<path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line>',
            fitness: '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>',
            pod: '<path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>',
            club: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle>',
            agency: '<rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>',
            invest: '<line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>',
            news: '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>',
            edu: '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>',
            book: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>',
            info: '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>',
            contact: '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.05 12.05 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.03 12.03 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>',
            edit: '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>',
            set: '<circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>',
            out: '<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line>'
        };

        // Helper to make item HTML
        const mkItem = (label, icon, active=false) => `
            <div class="nav-item ${active ? 'active' : ''}" data-id="${label.toLowerCase().replace(/\s+/g, '-')}">
                <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${icons[icon]}</svg>
                <span class="nav-text">${label}</span>
                <span class="tooltip-text">${label}</span>
            </div>
        `;

        this.element.innerHTML = `
            <!-- Close Button -->
            <div id="sidebarClose" tabindex="0" role="button" aria-label="Close menu">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span class="tooltip-text">Close menu</span>
            </div>

            <!-- Open Button -->
            <div id="sidebarOpen" role="button" aria-label="Open sidebar">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
                <span class="tooltip-text">Open menu</span>
            </div>

            <!-- Primary Section -->
            <div class="sidebar-section top-section">
                ${mkItem('Message', 'msg')}
                ${mkItem('Tiago AI', 'ai')}
                ${mkItem('Stream', 'stream')}
            </div>

            <!-- Main Section -->
            <div class="sidebar-section">
                ${mkItem('Cups', 'cup')}
                ${mkItem('Shop', 'shop')}
                ${mkItem('Fitness', 'fitness')}
                ${mkItem('Podcast', 'pod')}
                ${mkItem('Clubs', 'club')}
                ${mkItem('Agency', 'agency')}
                
                <!-- Hidden Items -->
                <div id="moreMenu">
                    ${mkItem('Invest', 'invest')}
                    ${mkItem('Newsletter', 'news')}
                    ${mkItem('Education', 'edu')}
                    ${mkItem('Books', 'book')}
                    ${mkItem('About Us', 'info')}
                    ${mkItem('Contact Us', 'contact')}
                </div>

                <!-- See More Button -->
                <div class="nav-item" id="seeMoreBtn">
                    <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="transition: transform 0.3s;">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                    <span class="nav-text">See more</span>
                </div>
            </div>

            <!-- Footer Section -->
            <div class="sidebar-section">
                ${mkItem('Profile', 'edit')}
                ${mkItem('Settings', 'set')}
                ${mkItem('Help', 'help')}
                ${mkItem('Sign Out', 'out')}
            </div>
        `;
    }

    attachEvents() {
        // 0. Item Click Handling
        this.element.addEventListener('click', (e) => {
            const navItem = e.target.closest('.nav-item');
            if (navItem && !navItem.id) { // Exclude special buttons like seeMoreBtn if they have IDs
                const id = navItem.dataset.id;
                console.log('Sidebar clicked:', id); // Debug log
                if (id && this.options.onItemClick) {
                    console.log('Sidebar: Item clicked:', id);
                    this.options.onItemClick(id, navItem);
                }
                
                // FIX: Ensure global app routing is triggered
                window.dispatchEvent(new CustomEvent('fg:navigate', { detail: { page: id } }));
                this.setActive(id);
            }
        });

        // 1. Toggle Button (Delegation)
        document.addEventListener('click', (e) => {
            const toggle = e.target.closest(`#${this.toggleId}`);
            if (toggle) {
                e.preventDefault();
                e.stopPropagation();
                this.toggle();
            }
        });

        // Close Button
        const closeBtn = this.element.querySelector('#sidebarClose');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => this.close());
            closeBtn.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.close();
                }
            });
        }

        // Open Button
        const openBtn = this.element.querySelector('#sidebarOpen');
        if (openBtn) {
            openBtn.addEventListener('click', () => this.open());
        }

        // 2. Overlay Click (Close)
        this.overlay.addEventListener('click', () => this.close());

        // 3. See More Click
        const seeMoreBtn = this.element.querySelector('#seeMoreBtn');
        const moreMenu = this.element.querySelector('#moreMenu');
        if (seeMoreBtn && moreMenu) {
            seeMoreBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                const isHidden = moreMenu.style.display !== 'block';
                
                if (isHidden) {
                    moreMenu.style.display = 'block';
                    seeMoreBtn.querySelector('.nav-text').textContent = 'See less';
                    seeMoreBtn.querySelector('svg').style.transform = 'rotate(180deg)';
                    // Move button to end of list
                    moreMenu.parentNode.appendChild(seeMoreBtn);
                } else {
                    moreMenu.style.display = 'none';
                    seeMoreBtn.querySelector('.nav-text').textContent = 'See more';
                    seeMoreBtn.querySelector('svg').style.transform = 'rotate(0deg)';
                }
            });
        }

        // 4. Prevent Scroll Chaining (The "Mouse Wheel Trap")
        this.element.addEventListener('wheel', (e) => {
            const el = this.element;
            const scrollTop = el.scrollTop;
            const scrollHeight = el.scrollHeight;
            const height = el.clientHeight;
            const delta = e.deltaY;
            const isScrollable = scrollHeight > height;

            if (!isScrollable) {
                // If content fits, no scroll needed -> prevent body scroll
                e.preventDefault();
                return;
            }

            // If at top and scrolling up
            if (delta < 0 && scrollTop <= 0) {
                e.preventDefault();
            }
            // If at bottom and scrolling down
            if (delta > 0 && scrollTop + height >= scrollHeight - 1) {
                e.preventDefault();
            }
            e.stopPropagation();
        }, { passive: false });
    }

    toggle() {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            this.element.classList.add('open');
            this.overlay.classList.add('active');
        } else {
            this.element.classList.remove('open');
            this.overlay.classList.remove('active');
        }
    }

    open() {
        this.isOpen = true;
        this.element.classList.add('open');
        this.overlay.classList.add('active');
    }

    close() {
        this.isOpen = false;
        this.element.classList.remove('open');
        this.overlay.classList.remove('active');
    }
}